import type { Result } from '../core/result';
import { ok, err } from '../core/result';
import type { Layout } from './types';
import type {
  FieldClass,
  FieldClassesFile,
  FieldRegion,
  FieldVotes,
} from './generated/fieldClasses';
import { createAjvFactory } from './validate';
import schema from './schema/fieldClasses.schema.json';

export type { FieldClass, FieldClassesFile, FieldRegion, FieldVotes };

// PLAN §4: farFields is how a field looks through the painter-view haze, a validation target and
// never an albedo.
export const FIELD_ALBEDO_CLASSES = [
  'nearFields',
  'polderFields',
  'gardenGrass',
] as const satisfies readonly FieldClass[];

export const MEADOW_REGION_ID = 'meadow';
export const NORTH_SHORE_REGION_ID = 'northShore';

// The layout features the ground partition turns into fields, by their layout ids; the meadow is
// whatever the bounds hold beyond every other feature.
export function fieldRegionIds(layout: Layout): readonly string[] {
  return [
    ...layout.polders.map((polder) => polder.id),
    ...layout.gardens.map((garden) => garden.id),
    MEADOW_REGION_ID,
    NORTH_SHORE_REGION_ID,
  ];
}

// The class most pixels voted for; a tie goes to the class listed first.
export function fieldWinner(votes: FieldVotes): FieldClass {
  return FIELD_ALBEDO_CLASSES.reduce((best, cls) => (votes[cls] > votes[best] ? cls : best));
}

export const UNVOTED_GARDEN_CLASS: FieldClass = 'gardenGrass';

// A garden keeps the class its own pixels voted for, however few: the nearest region that voted is a
// polder, whose class would paint the garden as a field. A garden none of whose pixels voted is garden
// ground.
export function gardenClass(votes: FieldVotes): FieldClass {
  const total = FIELD_ALBEDO_CLASSES.reduce((sum, cls) => sum + votes[cls], 0);
  return total > 0 ? fieldWinner(votes) : UNVOTED_GARDEN_CLASS;
}

function coverageErrors(file: FieldClassesFile, ids: readonly string[]): string[] {
  const known = new Set(ids);
  return [
    ...ids.filter((id) => !file.regions[id]).map((id) => `No class for field region ${id}`),
    ...Object.keys(file.regions)
      .filter((id) => !known.has(id))
      .map((id) => `Class for ${id}, which is no field region of the layout`),
  ];
}

function borrowingErrors(file: FieldClassesFile, gardens: ReadonlySet<string>): string[] {
  const { minPixels } = file.method;
  return Object.entries(file.regions).flatMap(([id, region]) => {
    if (!region) return [];
    if (gardens.has(id)) {
      return region.borrowedFrom === undefined
        ? []
        : [
            `${id} is a garden and borrows from ${region.borrowedFrom}; a garden keeps its own votes`,
          ];
    }
    if (region.borrowedFrom === undefined) {
      return region.pixels < minPixels
        ? [`${id} has ${region.pixels} voting pixels and borrows from no region`]
        : [];
    }
    const lender = file.regions[region.borrowedFrom];
    return [
      ...(region.pixels >= minPixels
        ? [`${id} has ${region.pixels} voting pixels and cannot borrow`]
        : []),
      ...(!lender || lender.pixels < minPixels
        ? [`${id} borrows from ${region.borrowedFrom}, which has too few pixels to lend`]
        : []),
      ...(lender && lender.class !== region.class
        ? [`${id} borrows ${region.class} from ${region.borrowedFrom}, which is ${lender.class}`]
        : []),
    ];
  });
}

function classErrors(id: string, region: FieldRegion, expected: FieldClass): string[] {
  if (region.class === expected) return [];
  return region.pixels > 0
    ? [`${id} takes ${region.class} and its pixels voted ${expected}`]
    : [
        `${id} has no voting pixels and takes ${region.class}; a garden none voted for is ${expected}`,
      ];
}

function voteErrors(id: string, region: FieldRegion, minPixels: number, garden: boolean): string[] {
  const votes = FIELD_ALBEDO_CLASSES.reduce((sum, cls) => sum + region.votes[cls], 0);
  if (votes !== region.pixels) {
    return [`${id} has ${String(region.pixels)} voting pixels and ${String(votes)} votes`];
  }
  if (region.borrowedFrom !== undefined) return [];
  if (garden) return classErrors(id, region, gardenClass(region.votes));
  return region.pixels < minPixels ? [] : classErrors(id, region, fieldWinner(region.votes));
}

// What the schema cannot see: the keys are exactly the layout's field regions, a region in frame takes
// the class most of its pixels voted for, a polder, the meadow or the north shore too thinly seen to
// vote takes the class of a region that voted, and a garden keeps its own votes however few.
export function checkFieldClasses(file: FieldClassesFile, layout: Layout): readonly string[] {
  const gardens = new Set(layout.gardens.map((garden) => garden.id));
  const votes = Object.entries(file.regions).flatMap(([id, region]) =>
    region ? voteErrors(id, region, file.method.minPixels, gardens.has(id)) : []
  );
  return [
    ...coverageErrors(file, fieldRegionIds(layout)),
    ...borrowingErrors(file, gardens),
    ...votes,
  ];
}

let validate: ReturnType<ReturnType<typeof createAjvFactory>['compile']> | null = null;

export function parseFieldClasses(
  value: unknown,
  layout: Layout
): Result<FieldClassesFile, readonly string[]> {
  validate ??= createAjvFactory().compile(schema);
  if (!validate(value)) {
    return err(
      (validate.errors ?? []).map((e) => `${e.instancePath || '/'}: ${e.message ?? 'invalid'}`)
    );
  }
  const file = value as FieldClassesFile;
  const errors = checkFieldClasses(file, layout);
  return errors.length > 0 ? err(errors) : ok(file);
}
